import { startOfWeek, endOfWeek, format, isWithinInterval, parseISO, eachDayOfInterval } from "date-fns";
import * as XLSX from "xlsx";

type Input = {
  percentage?: number;
  credits?: number;
  defaultCredits: number;
};

export default function calculateCreditsOrPercentage({
  percentage,
  credits,
  defaultCredits,
}: Input): number {
  // console.log("credits ", credits, " def credits ", defaultCredits)
  if (percentage !== undefined && credits !== undefined) {
    throw new Error("Provide either percentage or credits, not both");
  }

  if (percentage !== undefined) {
    // Calculate credits from percentage
    if (percentage < 0 || percentage > 100) {
      throw new Error("Percentage must be between 0 and 100");
    }
    return Math.round((percentage / 100) * defaultCredits);
  } else if (credits !== undefined) {
    // Calculate percentage from credits
    if (credits < 0 || credits > defaultCredits) {
      throw new Error("Credits must be between 0 and defaultCredits");
    }
    return (credits / defaultCredits) * 100;
  } else {
    throw new Error("Either percentage or credits must be provided");
  }
}

//export consumption


type ConsumptionEntry = {
  companyId: number;
  msisdn: string;
  date: string;
};

type UserConsumption = {
  msisdn: string;
  total: number;
  consumption: ConsumptionEntry[];
};

export type ConsumptionData = {
  total: number;
  consumption: UserConsumption[];
};


export function exportWeeklyConsumptionToExcel(
  data: ConsumptionData,
  selectedDate: Date,
  t: (key: string) => string // i18next translation function
) {
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
  const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });     // Sunday
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const rows: any[] = [];

  data.consumption.forEach((user) => {
    const dayCounts: Record<string, number> = {};
    let total = 0;

    // Init day columns
    weekDays.forEach((day) => {
      const dayStr = format(day, "EEEE");
      dayCounts[dayStr] = 0;
    });

    user.consumption.forEach((entry) => {
      const date = parseISO(entry.date);
      if (isWithinInterval(date, { start: weekStart, end: weekEnd })) {
        const dayStr = format(date, "EEEE");
        dayCounts[dayStr] += 1;
        total += 1;
      }
    });

    rows.push({
      [t("export.msisdn")]: user.msisdn,
      ...Object.fromEntries(
        weekDays.map((d) => [
          t("export." + format(d, "EEEE").toLowerCase()),
          dayCounts[format(d, "EEEE")],
        ])
      ),
      [t("export.total")]: total,
    });
  });

  const weekNumber = format(weekStart, "w");
  const customHeaderRow = [`${t("export.week")} ${weekNumber}`, t("export.weekly")];

  const columnHeaders = [
    t("export.msisdn"),
    ...weekDays.map((d) => t("export." + format(d, "EEEE").toLowerCase())),
    t("export.total")
  ];

  const worksheet = XLSX.utils.aoa_to_sheet([
    customHeaderRow,
    columnHeaders,
    ...rows.map((row) => columnHeaders.map((key) => row[key] ?? ""))
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `${t("export.week")} ${weekNumber}`);
  XLSX.writeFile(workbook, `consumption-week-${format(weekStart, "yyyy-MM-dd")}.xlsx`);
}


export type ConsumptionPerMsisdn = {
  msisdn: string;
  total: number;
  consumption: ConsumptionEntry[];
};

export function exportConsumptionToExcelNew(
  consumption: ConsumptionPerMsisdn[],
  weekStart: Date,
  t: (key: string) => string // i18next translation function
) {
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const rows = consumption.map((user) => {
    const dayCounts: Record<string, number> = {};
    weekDays.forEach((day) => {
      const dayStr = format(day, "EEEE");
      dayCounts[dayStr] = 0;
    });

    let total = 0;
    user.consumption.forEach((entry) => {
      const date = parseISO(entry.date);
      if (isWithinInterval(date, { start: weekStart, end: weekEnd })) {
        const dayStr = format(date, "EEEE");
        dayCounts[dayStr]++;
        total++;
      }
    });

    return {
      [t("export.msisdn")]: user.msisdn,
      ...Object.fromEntries(weekDays.map((d) => [t("export." + format(d, "EEEE").toLowerCase()), dayCounts[format(d, "EEEE")]])),
      [t("export.total")]: total,
    };
  });

  const weekNumber = format(weekStart, "w");
  const customHeaderRow = [`${t("export.week")} ${weekNumber}`, t("export.weekly")];

  const columnHeaders = [
    t("export.msisdn"),
    ...weekDays.map((d) => t("export." + format(d, "EEEE").toLowerCase())),
    t("export.total")
  ];

  const worksheet = XLSX.utils.aoa_to_sheet([
    customHeaderRow,
    columnHeaders,
    ...rows.map((row) => columnHeaders.map((key) => row[key] ?? ""))
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `${t("export.week")} ${weekNumber}`);
  XLSX.writeFile(workbook, `consumption-week-${format(weekStart, "yyyy-MM-dd")}.xlsx`);
}
export function exportRangeConsumptionToExcel(
  data: ConsumptionData,
  startDate: Date,
  endDate: Date,
  selectedMsisdns: string[],
  t: (key: string) => string
) {
  const daysInRange = eachDayOfInterval({ start: startDate, end: endDate });

  // Create header labels like "28.06", "29.06", ...
  const dayLabels = daysInRange.map((d) => format(d, "dd.MM"));

  // Map from ISO date string to day label for quick lookup
  const isoToDayLabel = daysInRange.reduce<Record<string, string>>((acc, day) => {
    acc[format(day, "yyyy-MM-dd")] = format(day, "dd.MM");
    return acc;
  }, {});

  // Prepare rows for selected MSISDNs only
  const rows = data.consumption
    .filter((user) => selectedMsisdns.includes(user.msisdn))
    .map((user) => {
      // Initialize counts by day label, e.g. { "28.06": 0, "29.06": 0, ... }
      const dayCounts: Record<string, number> = {};
      dayLabels.forEach((label) => {
        dayCounts[label] = 0;
      });

      let total = 0;
      user.consumption.forEach((entry) => {
        const dateStr = entry.date.slice(0, 10); // yyyy-MM-dd
        if (dateStr >= format(startDate, "yyyy-MM-dd") && dateStr <= format(endDate, "yyyy-MM-dd")) {
          const dayLabel = isoToDayLabel[dateStr];
          if (dayLabel) {
            dayCounts[dayLabel]++;
            total++;
          }
        }
      });

      return {
        [t("export.msisdn")]: user.msisdn,
        ...dayLabels.reduce((obj, label) => {
          obj[label] = dayCounts[label]; // 0 or count
          return obj;
        }, {} as Record<string, number>),
        [t("export.total")]: total,
      };
    });

  // Headers: MSISDN + day labels + total
  const columnHeaders = [t("export.msisdn"), ...dayLabels, t("export.total")];

  // Convert rows to arrays matching headers order
  const sheetRows = rows.map((row) => columnHeaders.map((col) => row[col] ?? 0));

  // Custom header row with date range label
  const customHeaderRow = [
    `${t("export.range")}: ${format(startDate, "yyyy-MM-dd")} - ${format(endDate, "yyyy-MM-dd")}`,
  ];

  const worksheet = XLSX.utils.aoa_to_sheet([
    customHeaderRow,
    columnHeaders,
    ...sheetRows,
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Consumption Range");

  XLSX.writeFile(workbook, `consumption-range-${format(startDate, "yyyy-MM-dd")}_to_${format(endDate, "yyyy-MM-dd")}.xlsx`);
}
